import * as echarts from 'echarts'
import React from 'react'
import { ChartArea, chartValueLabelStyle, classToFComponent, LifesHubColors } from 'src/utils/views'
import { makeStyles } from 'tss-react/mui'
import { JSX } from 'react/jsx-runtime'

import { EChartViewClass } from '../../../the-consumer-market/views/helpers/base-echart'
import AccessCard, { AccessCardHeader, AccessEmptyState } from './aa_card'
import { AccessChartScope } from '../aa_scopes'
import { formatMonthLabel, formatNumber } from 'src/utils/formatter-utils'
import { formatNumberToCompactShortForm } from '../../../the-consumer-market/views/helpers/tcm_view_utils'

const INK_SECONDARY = LifesHubColors.muted
const AXIS_LINE = LifesHubColors.border
const GRID_LINE = LifesHubColors.gridLine

const useStyles = makeStyles()({
    view: {
        display: 'flex',
        alignSelf: 'stretch',
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 0,
        minHeight: 200
    }
})

type ChartMonthlyProps = {
    className?: string
    style?: React.CSSProperties
    scope: AccessChartScope
}

class ChartMonthlyViewClass extends EChartViewClass<ChartMonthlyProps> {
    // :: Fields

    scope!: AccessChartScope
    classes!: ReturnType<typeof useStyles>['classes']

    onSyncState(props: ChartMonthlyProps) {
        this.scope = props.scope
        this.classes = useStyles().classes
        this.chartData = buildChartData(this.scope)
        this.loaded = this.scope.loaded
        super.onSyncState(props)
    }

    override render({ className, style, scope }: ChartMonthlyProps): React.JSX.Element {
        return (
            <AccessCard elevation={0} className={className} style={style}>
                <AccessCardHeader title={scope.caption || 'Evolução mensal'} />
                {scope.loaded && scope.rows.length === 0 ? (
                    <AccessEmptyState
                        title="Sem acessos no período"
                        text="Nenhum mês do intervalo selecionado registrou acesso."
                    />
                ) : (
                    <ChartArea>{super.render({ className: this.classes.view, scope })}</ChartArea>
                )}
            </AccessCard>
        )
    }
}

export const AccessChartMonthlyView = classToFComponent(ChartMonthlyViewClass, React)
export default AccessChartMonthlyView

function buildChartData(scope: AccessChartScope): echarts.EChartsOption {
    const labels = scope.rows.map((row) => formatMonthLabel(row.period))
    const values = scope.rows.map((row) => row.accesses)

    const showBarLabels = values.length <= 14

    return {
        grid: { left: 8, right: 12, top: 28, bottom: 4, containLabel: true },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            valueFormatter: (value) => formatNumber(Number(value))
        },
        xAxis: {
            type: 'category',
            data: labels,
            axisTick: { show: false },
            axisLine: { lineStyle: { color: AXIS_LINE } },
            axisLabel: { color: INK_SECONDARY }
        },
        yAxis: {
            type: 'value',
            splitLine: { lineStyle: { color: GRID_LINE } },
            axisLabel: { color: INK_SECONDARY, formatter: (value: number) => formatNumberToCompactShortForm(value) }
        },
        series: [
            {
                name: 'Acessos',
                type: 'bar',
                data: values,
                barMaxWidth: 32,
                itemStyle: { color: LifesHubColors.chartBase, borderRadius: [4, 4, 0, 0] },
                label: {
                    show: showBarLabels,
                    position: 'top',
                    color: chartValueLabelStyle.color,
                    formatter: (params) => formatNumberToCompactShortForm(Number(params.value))
                }
            }
        ]
    }
}
